import * as React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getMatchAdminStat, updateMatchAdminStats } from '../../Api';
import AdminMatchStatDto from '../../models/dtos/AdminMatchStatDto';
import { CenteredProgress, EmptyState, formatLabel } from './MatchAdminTabCommon';
import { STAT_ITEM_TYPE_OPTIONS, STAT_UNIT_TYPE_OPTIONS } from './matchAdminOptions';

type EditableStatRow = {
    rowId: string;
    type: string;
    unitType: string;
    homeStat: string;
    awayStat: string;
};

function toDraftRows(stats: AdminMatchStatDto['stats']): EditableStatRow[] {
    return stats.map((item, index) => ({
        rowId: `${item.type ?? 'stat'}-${index}`,
        type: item.type ?? '',
        unitType: item.unitType ?? 'NONE',
        homeStat: item.homeStat?.toString() ?? '0',
        awayStat: item.awayStat?.toString() ?? '0'
    }));
}

function toPayload(rows: EditableStatRow[]): AdminMatchStatDto['stats'] {
    return rows
        .filter(row => row.type.length > 0)
        .map(row => ({
            type: row.type,
            unitType: row.unitType,
            homeStat: Number(row.homeStat),
            awayStat: Number(row.awayStat)
        }));
}

function SortableStatRow(props: {
    row: EditableStatRow;
    takenByOthers: Set<string>;
    onUpdate: (patch: Partial<EditableStatRow>) => void;
    onDelete: () => void;
}) {
    const { row, takenByOthers, onUpdate, onDelete } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.rowId });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 32, cursor: 'grab', color: 'text.disabled' }} {...attributes} {...listeners}>
                <DragHandleIcon fontSize="small" />
            </TableCell>
            <TableCell sx={{ minWidth: 240 }}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    value={row.type}
                    onChange={event => onUpdate({ type: event.target.value })}
                >
                    {STAT_ITEM_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option} disabled={takenByOthers.has(option)}>
                            {formatLabel(option)}
                        </MenuItem>
                    ))}
                </TextField>
            </TableCell>
            <TableCell sx={{ minWidth: 130 }}>
                <TextField size="small" type="number" fullWidth value={row.homeStat} onChange={event => onUpdate({ homeStat: event.target.value })} />
            </TableCell>
            <TableCell sx={{ minWidth: 130 }}>
                <TextField size="small" type="number" fullWidth value={row.awayStat} onChange={event => onUpdate({ awayStat: event.target.value })} />
            </TableCell>
            <TableCell sx={{ minWidth: 170 }}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    value={row.unitType}
                    onChange={event => onUpdate({ unitType: event.target.value })}
                >
                    {STAT_UNIT_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                            {formatLabel(option)}
                        </MenuItem>
                    ))}
                </TextField>
            </TableCell>
            <TableCell align="right">
                <IconButton color="error" onClick={onDelete}>
                    <DeleteOutlineIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

export default function MatchStatisticsTab(props: {
    matchId: number;
    onError: (message: string) => void;
    onSuccess: (message: string) => void;
}) {
    const { matchId, onError, onSuccess } = props;
    const [adminStat, setAdminStat] = React.useState<AdminMatchStatDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [draftRows, setDraftRows] = React.useState<EditableStatRow[]>([]);
    const [saving, setSaving] = React.useState(false);

    const sensors = useSensors(useSensor(PointerSensor));

    React.useEffect(() => {
        setLoading(true);
        getMatchAdminStat(matchId)
            .then(result => { setAdminStat(result); setLoading(false); })
            .catch(() => { setAdminStat(null); setLoading(false); });
    }, [matchId]);

    React.useEffect(() => {
        setDraftRows(adminStat?.stats ? toDraftRows(adminStat.stats) : []);
    }, [adminStat]);

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over != null && active.id !== over.id) {
            setDraftRows(current => {
                const oldIndex = current.findIndex(row => row.rowId === active.id);
                const newIndex = current.findIndex(row => row.rowId === over.id);
                return arrayMove(current, oldIndex, newIndex);
            });
        }
    };

    if (loading) {
        return <CenteredProgress />;
    }

    if (adminStat == null) {
        return <EmptyState message="No statistics available for the main event." />;
    }

    const payload = toPayload(draftRows);
    const originalPayload = adminStat.stats;
    const isDirty = JSON.stringify(payload) !== JSON.stringify(originalPayload);
    const usedTypes = draftRows.map(row => row.type).filter(Boolean);
    const addableTypes = STAT_ITEM_TYPE_OPTIONS.filter(type => !usedTypes.includes(type));

    const updateRow = (rowId: string, patch: Partial<EditableStatRow>) => {
        setDraftRows(current => current.map(row => row.rowId === rowId ? { ...row, ...patch } : row));
    };

    const onAddRow = () => {
        const nextType = addableTypes[0];
        if (nextType == null) {
            onError('All supported stat types are already used.');
            return;
        }

        setDraftRows(current => current.concat({
            rowId: `new-${Date.now()}-${current.length}`,
            type: nextType,
            unitType: 'INT',
            homeStat: '0',
            awayStat: '0'
        }));
    };

    const onReset = () => {
        setDraftRows(toDraftRows(adminStat.stats));
    };

    const onSave = async () => {
        const uniqueTypes = new Set(payload.map(item => item.type));
        if (uniqueTypes.size !== payload.length) {
            onError('Stat types must be unique.');
            return;
        }

        setSaving(true);
        try {
            const result = await updateMatchAdminStats(matchId, payload);
            setAdminStat(prev => prev == null ? prev : { ...prev, stats: result.stats });
            onSuccess('Statistics update sent to provider.');
        } catch (error) {
            onError(error instanceof Error ? error.message : 'Failed to update statistics.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
                Main variety: {adminStat.varietyName}
            </Typography>
            <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddRow}>Add stat</Button>
                <Button variant="outlined" color="inherit" startIcon={<RestartAltIcon />} onClick={onReset} disabled={!isDirty || saving}>Reset</Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={!isDirty || saving}>Save</Button>
            </Stack>
            {draftRows.length === 0 ? (
                <EmptyState message="No statistics available for the main event." />
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={draftRows.map(r => r.rowId)} strategy={verticalListSortingStrategy}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 32 }} />
                                        <TableCell>Type</TableCell>
                                        <TableCell>Home</TableCell>
                                        <TableCell>Away</TableCell>
                                        <TableCell>Unit</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {draftRows.map(row => {
                                        const takenByOthers = new Set(
                                            draftRows
                                                .filter(candidate => candidate.rowId !== row.rowId)
                                                .map(candidate => candidate.type)
                                                .filter(Boolean)
                                        );

                                        return (
                                            <SortableStatRow
                                                key={row.rowId}
                                                row={row}
                                                takenByOthers={takenByOthers}
                                                onUpdate={patch => updateRow(row.rowId, patch)}
                                                onDelete={() => setDraftRows(current => current.filter(item => item.rowId !== row.rowId))}
                                            />
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </SortableContext>
                    </DndContext>
                </Box>
            )}
        </Stack>
    );
}
